<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  title: { type: String, default: '' },
  permalink: { type: String, default: '' },
  thumbnail: { type: String, default: '' },
  price: { type: String, default: '' },
  stock: { type: [Number, String], default: null },
  itemId: { type: String, default: '' },
});

const { t } = useI18n();
const thumbnailFailed = ref(false);

const displayTitle = computed(() => props.title || props.itemId);
const showThumbnail = computed(() => props.thumbnail && !thumbnailFailed.value);
const hasStock = computed(
  () => props.stock !== null && props.stock !== undefined && props.stock !== ''
);
const tag = computed(() => (props.permalink ? 'a' : 'div'));
const linkAttrs = computed(() => {
  if (!props.permalink) return {};
  return {
    href: props.permalink,
    target: '_blank',
    rel: 'noopener noreferrer nofollow',
  };
});

const onThumbnailError = () => {
  thumbnailFailed.value = true;
};
</script>

<template>
  <component
    :is="tag"
    v-bind="linkAttrs"
    class="flex items-center gap-3 min-w-0 px-3 py-2 mx-2 mt-2 rounded-lg border border-n-slate-4 bg-n-solid-2 hover:bg-n-alpha-2"
    :title="displayTitle"
  >
    <img
      v-if="showThumbnail"
      :src="thumbnail"
      :alt="displayTitle"
      class="object-cover rounded-md size-14 shrink-0 bg-n-alpha-2"
      @error="onThumbnailError"
    />
    <div
      v-else
      class="flex items-center justify-center rounded-md size-14 shrink-0 bg-n-alpha-2"
    >
      <Icon icon="i-ri-shopping-bag-3-line" class="text-n-slate-11 size-5" />
    </div>
    <div class="flex flex-col min-w-0 gap-0.5">
      <span class="text-xs font-medium text-n-slate-11">
        {{ t('CONVERSATION.MERCADOLIBRE_ITEM.LABEL') }}
      </span>
      <span class="text-sm font-medium truncate text-n-slate-12">
        {{ displayTitle }}
      </span>
      <span v-if="price || hasStock" class="text-xs text-n-slate-11">
        <template v-if="price">{{ price }}</template>
        <template v-if="price && hasStock"> · </template>
        <template v-if="hasStock">
          {{ t('CONVERSATION.MERCADOLIBRE_ITEM.STOCK', { count: stock }) }}
        </template>
      </span>
    </div>
  </component>
</template>
